import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

// Stripe stuff
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { withStyles } from "@mui/styles";
import MuiTextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import { Button, GridContainerWithSpacing as GridContainer, GridItemWithSpacing as GridItem } from "~/shared/components/MuiComponents.jsx";

import KickClient from "~/visits/utils/api.js";
import {
  getOrderComplete,
  getPromoCode,
  getVisitDifferentPharmacy,
  getVisitEmail,
  getVisitFullName,
  getVisitID,
  getVisitPhone,
} from "~/visits/utils/visitCookies.js";
import { useVisitIDRequired } from "~/visits/utils/visitUtils";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import { isConsultationTypeInsomniaInitial } from "~/shared/utils/sharedUtils";

import { CheckoutProgress } from "../shared.jsx";
import {
  SocialProof,
  DoctorGuaranteeSection,
  WhatHappensNext,
  SleepConciergeInfo,
  SelectedSleepMeds,
} from "./paymentComponents.jsx";
import {
  checkoutPostData,
  completeCheckoutAndRedirect,
  getStripeOptions,
  paymentDetailsFormValidationSchema,
  orderTotalPriceWithCouponDiscount,
} from "./paymentHelpers.js";

import consultationStyles, {
  VISIT_MAX_WIDTH,
} from "~/visits/styles/consultationStyles.js";
import { kickNavy, kickAmaOrange } from "~/shared/styles/baseStyles.js";

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
      betas: ["link_default_integration_beta_1"],
    });
  }
  return stripePromise;
};

const PaymentDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");

  useVisitIDRequired(checkoutSessionID);
  if (!checkoutSessionID || !getVisitID(checkoutSessionID)) {
    window.location.href = "/me/portal";
    return null;
  }

  const [creditCardError, setMyCreditCardError] = useState("");
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState("");
  const [{ cardName }, setFormState] = useState({
    cardName: getVisitFullName(),
  });

  // grab setup intent client secret from server
  useEffect(() => {
    // https://devtrium.com/posts/async-functions-useeffect
    if (getOrderComplete(checkoutSessionID)) {
      // if order is complete, don't let user go back to payment page
      props.history.push(
        `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`
      );
      window.location.href = `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`;
    }
    console.log(
      "getVisitDifferentPharmacy: ",
      getVisitDifferentPharmacy(checkoutSessionID)
    );

    const fetchData = async () => {
      // get the client secret from server
      const client = KickClient.newAuthenticatedClient();
      const setupIntentResult = await client.get("/setup-intent");
      console.log("setupIntentResult.data: ", setupIntentResult.data);

      setSetupIntentClientSecret(setupIntentResult.data);
    };

    if (!setupIntentClientSecret) {
      fetchData().catch(console.error);
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  });

  // buyWithInvoice handles both subscription and non-subscription billing
  const buyWithInvoice = async (values, stripe, elements) => {
    const promoCode = getPromoCode(checkoutSessionID);
    const postData = checkoutPostData(
      values,
      consultationType,
      checkoutSessionID,
      promoCode
    );

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: "https://www.gokick.com/",
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    if (error) {
      // just a validation error - prevent page from refreshing
      if (error.type == "validation_error") {
        console.log("just return validation error");
        return;
      }

      setFormState(values); // save current form state to re-populate - call after grabbing react values
      setMyCreditCardError(error.message);
      return false;
    }
    const paymentMethodID = setupIntent.payment_method;

    setFormState(values); // save current form state to re-populate - call after grabbing react values

    await completeCheckoutAndRedirect({
      paymentMethodID,
      postData,
      checkoutSessionID,
      consultationType,
      history: props.history,
      setError: setMyCreditCardError,
    });
  };

  const BillingForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [paymentRequest, setPaymentRequest] = useState(null); // for apple pay

    const {
      control,
      handleSubmit,
      getValues,
      trigger,
      formState: { isSubmitting, errors },
    } = useForm({
      resolver: yupResolver(paymentDetailsFormValidationSchema),
    });

    // pay using apple or google or msft pay
    const payUsingStripePaymentRequest = async (paymentMethod) => {
      const promoCode = getPromoCode(checkoutSessionID);
      const postData = checkoutPostData(
        getValues(),
        consultationType,
        checkoutSessionID,
        promoCode
      );

      setFormState(getValues()); // save current form state to re-populate - call after grabbing react values

      await completeCheckoutAndRedirect({
        paymentMethodID: paymentMethod.id,
        postData,
        checkoutSessionID,
        consultationType,
        history: props.history,
        setError: setMyCreditCardError,
      });
    };

    useEffect(() => {
      // hook up Stripe PaymentRequest Button for Google / Apple Pay
      if (stripe) {
        const pr = stripe.paymentRequest({
          country: "US",
          currency: "usd",
          total: {
            label: "",
            amount:
              parseInt(
                orderTotalPriceWithCouponDiscount(true, checkoutSessionID)
              ) * 100 || 0, // stripe expects value in cents
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        // Check the availability of the Payment Request API.
        pr.canMakePayment().then((result) => {
          if (result) {
            console.log("pr.canMakePayment result: ", result);
            setPaymentRequest(pr);

            pr.on("cancel", () => {
              // handle cancel event
            });

            pr.on("paymentmethod", async (event) => {
              const setupResult = await stripe.confirmCardSetup(
                setupIntentClientSecret.clientSecret,
                {
                  payment_method: event.paymentMethod.id,
                }
              );

              console.log(
                "returned confirmCardSetup setupResult:",
                setupResult
              );

              try {
                const kickResult = await payUsingStripePaymentRequest(
                  event.paymentMethod
                );

                if (kickResult.error) {
                  bugsnagClient.user = {
                    email: getVisitEmail(),
                  };
                  bugsnagClient.notify(new Error(kickResult.error));

                  event.complete("fail");
                }

                event.complete("success");
              } catch (e) {
                console.error("Error [payUsingStripePaymentRequest]: ", e);
              }
            });
          }
        });
      }
    }, [stripe]);

    return (
      <form
        onSubmit={handleSubmit(async (data) => {
          console.log(errors);
          try {
            await buyWithInvoice(data, stripe, elements);
          } catch (e) {
            console.log("buyWithInvoice error: ", e);
          }
        })}
        style={{ maxWidth: VISIT_MAX_WIDTH }}
      >
        <div>
          <div>
            <Controller
              render={({ field }) => (
                <MuiTextField
                  {...field}
                  label="Cardholder Name"
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
              )}
              name="cardName"
              control={control}
              defaultValue={cardName}
            />

            <p style={{ color: "red" }}>{errors.cardName?.message}</p>

            <div
              style={{
                border: "1px solid #C4C4C4",
                borderRadius: "4px",
                margin: "6px 0 0 0",
                padding: "20px 8px",
                minWidth: "350px", // requird for mobile
              }}
            >
              <Controller
                name="stripe"
                control={control}
                defaultValue={null}
                render={(field) => (
                  <PaymentElement
                    options={{
                      defaultValues: {
                        billingDetails: {
                          email: getVisitEmail(),
                          name: getVisitFullName(),
                          phone: getVisitPhone(),
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
            </div>
            <p
              style={{
                color: "red",
                marginTop: "15px",
                marginBottom: "10px",
                fontSize: "17px",
              }}
            >
              {creditCardError}

              {!isEmpty(errors) && "Please fill out all the fields above"}
            </p>
          </div>

          <DoctorGuaranteeSection classes={classes} />

          <Button
            type="submit"
            disabled={isSubmitting}
            className={classes.confirmAndPayButton}
            style={{
              marginTop: "30px",
              fontSize: "18px",
              fontWeight: "bold",
              textTransform: "unset",
              backgroundColor: kickAmaOrange,
              color: kickNavy,
            }}
          >
            Confirm
            {isSubmitting ? (
              <CircularProgress
                size="large"
                style={{
                  position: "relative",
                  left: "24px",
                  top: "1px",
                  color: kickNavy,
                }}
              />
            ) : null}
          </Button>

          {paymentRequest && !isSubmitting && (
            <div className={classes.stripePaymentRequestButtonBlock}>
              <p style={{ textAlign: "center", margin: "0 0 10px 0" }}>
                — or —
              </p>
              <PaymentRequestButtonElement
                options={{ paymentRequest }}
                onClick={async (event) => {
                  // attempt to validate form before showing paymentRequest panel
                  event.preventDefault();
                  const result = await trigger();
                  if (result) {
                    paymentRequest.show();
                  }
                }}
              />
            </div>
          )}
        </div>
      </form>
    );
  };

  if (!setupIntentClientSecret) {
    return null;
  }

  return (
    <Elements
      stripe={getStripe()}
      options={getStripeOptions(setupIntentClientSecret)}
    >
      <div className={`${classes.containerWithSpacing} payment-section`}>
        <div className={classes.checkoutProgressMobile}>
          <CheckoutProgress step={2} checkoutSessionID={checkoutSessionID} />
        </div>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <SocialProof
              style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
              showtestimonials={isConsultationTypeInsomniaInitial(
                consultationType
              ).toString()}
            />
            {isConsultationTypeInsomniaInitial(consultationType) && (
              <>
                <WhatHappensNext />
                <SleepConciergeInfo />
                <SelectedSleepMeds />
              </>
            )}
          </GridItem>
          <GridItem xs={12} md={8}>
            <div className={classes.paymentForm}>
              <div className={classes.checkoutProgressDesktop}>
                <CheckoutProgress
                  step={2}
                  checkoutSessionID={checkoutSessionID}
                />
              </div>
              <BillingForm />
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </Elements>
  );
};

export default withStyles(consultationStyles)(PaymentDetails);
